// @flow strict
import * as React from "react"
import { connect } from "react-redux"
import InputField from "@kiwicom/orbit-components/lib/InputField"
import CloseIcon from "@kiwicom/orbit-components/lib/icons/Close"
import SearchIcon from "@kiwicom/orbit-components/lib/icons/Search"
import Stack from "@kiwicom/orbit-components/lib/Stack"
import * as R from "ramda"
import type { Dispatch } from "redux"
import { set } from "splitster-redux"
import styled from "styled-components"
import type { ThemeProps } from "@kiwicom/nitro/lib/records/Theme"
import { themeDefault } from "@kiwicom/nitro/lib/records/Theme"
import Cookies from "js-cookie"
import Button from "@kiwicom/orbit-components/lib/Button"
import Toggle from "@kiwicom/nitro/lib/components/Toggle"
import Heading from "@kiwicom/orbit-components/lib/Heading"
import Text from "@kiwicom/orbit-components/lib/Text"

import type { Splitster } from "./records/Splitster"
import Table from "./Table"

const REASON_TEXTS = {
  usage: "usage number not satisfied",
  separate_test: "other test is running separated",
  user_group: "user group is not satisfied",
  config: "specified in config file",
}

type Props = {|
  dispatch: Dispatch<any>,
  splitster: Splitster,
|}

type State = {
  query: string,
}

const ToggleWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: ${({ theme }: ThemeProps) => theme.orbit.spaceSmall} 0;
`

ToggleWrapper.defaultProps = {
  theme: themeDefault,
}

const Wrapper = styled.div`
  padding: ${({ theme }: ThemeProps) => theme.orbit.spaceSmall};
`

Wrapper.defaultProps = {
  theme: themeDefault,
}

const Config = styled.pre`
  background-color: ${({ theme }: ThemeProps) => theme.orbit.paletteWhite};
  max-width: 100%;
  padding: ${({ theme }: ThemeProps) => theme.orbit.spaceSmall};
`

Config.defaultProps = {
  theme: themeDefault,
}

const QUERY_INPUT_NAME = "debugSplitsterQueryInput"

class SplitsterDebug extends React.PureComponent<Props, State> {
  state = {
    query: "",
  }

  inputRef: { current: React$ElementRef<any> } = React.createRef()

  componentDidMount() {
    if (this.inputRef) {
      this.inputRef.current.focus()
    }
  }

  handleQueryChange = event => {
    this.setState({ query: event.target.value })
  }

  handleQueryClear = event => {
    event.preventDefault()
    this.setState({ query: "" })

    if (this.inputRef) {
      this.inputRef.current.focus()
    }
  }

  handleChange = testId => variantId => {
    const { dispatch } = this.props
    dispatch(set(testId, variantId, true))
  }

  render() {
    const { query } = this.state
    const { splitster } = this.props

    const tests = R.compose(
      R.map(([key, item]) => ({
        ...item,
        id: key,
      })),
      Object.entries,
    )(splitster.tests)

    const uppercasedQuery = query.toUpperCase()

    return (
      <Wrapper>
        <InputField
          size="small"
          type="text"
          name={QUERY_INPUT_NAME}
          label="Search splitster test"
          value={query}
          ref={this.inputRef}
          placeholder="Test id or its id part"
          prefix={<SearchIcon />}
          suffix={
            query.length ? (
              <a href="#" onClick={this.handleQueryClear}>
                <CloseIcon />
              </a>
            ) : null
          }
          onChange={this.handleQueryChange}
        />

        <ToggleWrapper>
          <Stack spacing="compact">
            {R.compose(
              R.map(test => (
                <div key={test.id}>
                  <Stack direction="row" align="center" justify="between" spaceAfter="normal">
                    <Heading element="h3" type="title5">
                      {test.id}
                    </Heading>

                    {Cookies.get(`splitster_${test.id}_${test.version}`) && (
                      <Button
                        size="small"
                        type="secondary"
                        onClick={() => {
                          Cookies.remove(`splitster_${test.id}_${test.version}`)
                          this.forceUpdate()
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Stack>

                  {test.description && <Text>{test.description}</Text>}
                  {test.disabled && (
                    <Text>
                      <strong>Disabled:</strong> {REASON_TEXTS[test.disabledReason]}
                    </Text>
                  )}
                  <Stack>
                    <Table test={test} handleChange={this.handleChange(test.id)} />
                    <Stack direction="row" justify="between" spaceAfter="normal">
                      <Toggle>
                        {({ open, onToggle }) => (
                          <Stack direction="column" spacing="condensed">
                            <Button size="small" type="secondary" onClick={onToggle}>
                              {open ? "Hide" : "Show"} config detail
                            </Button>
                            {open && <Config>{JSON.stringify(test, null, 1)}</Config>}
                          </Stack>
                        )}
                      </Toggle>
                    </Stack>
                  </Stack>
                </div>
              )),
              R.filter(item => item.id.includes(uppercasedQuery)),
            )(tests)}
          </Stack>
        </ToggleWrapper>
      </Wrapper>
    )
  }
}

// $FlowFixMe TODO: plz add type when updating
export default connect(state => ({
  splitster: state.splitster,
}))(SplitsterDebug)
